import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MultiLangTextField from '../../components/MultiLangTextField';
import { getChapterById, createChapter, updateChapter, getProphetById } from '../../services/prophetService';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Box,
  makeStyles, CircularProgress, Snackbar, Typography, IconButton
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import CodeIcon from '@material-ui/icons/Code';
import MenuBookIcon from '@material-ui/icons/MenuBook';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'clean'],
    [{ 'color': [] }, { 'background': [] }]
  ]
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link',
  'color', 'background'
];

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      [theme.breakpoints.down('xs')]: {
        margin: 8,
        width: 'calc(100vw - 16px)',
        maxWidth: 'calc(100vw - 16px) !important',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(248, 250, 252, 0.9)',
    backdropFilter: 'blur(10px)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  headerIconBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
  },
  dialogTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
  },
  dialogSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: 24,
    flex: 1,
    overflowY: 'auto',
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  sectionCard: {
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.primary.main,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  dialogActions: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 16,
  },
  field: {
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.default,
      borderRadius: 12,
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
  },
  editorContainer: {
    marginBottom: theme.spacing(3),
    '& .ql-toolbar': {
      backgroundColor: theme.palette.background.default,
      borderColor: theme.palette.divider,
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      '& .ql-stroke': { stroke: theme.palette.text.secondary },
      '& .ql-fill': { fill: theme.palette.text.secondary },
      '& .ql-picker': { color: theme.palette.text.secondary },
    },
    '& .ql-container': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      borderBottomLeftRadius: 12,
      borderBottomRightRadius: 12,
      minHeight: 250,
      fontFamily: theme.typography.fontFamily,
      fontSize: '1rem',
      color: theme.palette.text.primary,
    },
  },
  editorLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }
}));

export default function ProphetChapterFormModal({
  open,
  prophetId,
  chapterId = null,
  onClose,
  onSuccess,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isEdit = Boolean(chapterId);

  const [prophetName, setProphetName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHTML, setShowHTML] = useState({ contentEn: false, contentBn: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [form, setForm] = useState({
    title: { en: '', bn: '' },
    content: { en: '', bn: '' },
  });

  useEffect(() => {
    if (!open || !prophetId) return;

    let mounted = true;
    setLoading(true);

    (async () => {
      try {
        const pData = await getProphetById(prophetId);
        if (pData && mounted) {
          setProphetName(pData.name?.en || pData.arabicName || 'Prophet');
        }
        if (chapterId) {
          const chData = await getChapterById(prophetId, chapterId);
          if (chData && mounted) {
            setForm({
              title: { en: chData.title?.en || '', bn: chData.title?.bn || '' },
              content: { en: chData.content?.en || '', bn: chData.content?.bn || '' },
            });
          }
        } else {
          setForm({
            title: { en: '', bn: '' },
            content: { en: '', bn: '' },
          });
        }
      } catch (err) {
        console.error('Error fetching chapter data:', err);
      }
      if (mounted) setLoading(false);
    })();

    return () => { mounted = false; };
  }, [open, prophetId, chapterId]);

  const handleSave = async () => {
    if (!form.title.en?.trim() && !form.title.bn?.trim()) {
      setSnackbar({ open: true, message: 'Chapter title is required' });
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateChapter(prophetId, chapterId, form);
        setSnackbar({ open: true, message: 'Chapter updated successfully' });
      } else {
        await createChapter(prophetId, form);
        setSnackbar({ open: true, message: 'Chapter created successfully' });
      }
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 500);
    } catch (err) {
      console.error('Save error:', err);
      setSnackbar({ open: true, message: 'Failed to save chapter' });
    }
    setSaving(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      className={classes.dialogRoot}
      fullWidth
      maxWidth={false}
      scroll="paper"
    >
      <div className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIconBadge}>
            <MenuBookIcon />
          </div>
          <div>
            <Typography className={classes.dialogTitle}>
              {isEdit ? 'Edit Chapter' : 'Add Chapter'} - {prophetName}
            </Typography>
            <Typography className={classes.dialogSubtitle}>
              {isEdit ? 'Update chapter title and story content' : 'Create a new chapter story for this prophet'}
            </Typography>
          </div>
        </div>
        <IconButton className={classes.closeBtn} onClick={() => !saving && onClose()} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <div className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                1. CHAPTER TITLE (English & Bangla)
              </Typography>
              <MultiLangTextField
                fields={form.title}
                onChange={(title) => setForm(prev => ({ ...prev, title }))}
                label="Chapter Title"
                helperText={{ en: 'e.g. Chapter 1: Early Life of Prophet', bn: 'যেমন: অধ্যায় ১: নবীর শৈশব' }}
              />
            </div>

            <div className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                2. CHAPTER CONTENT (English & Bangla)
              </Typography>

              <Box className={classes.editorContainer}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography className={classes.editorLabel}>English Chapter Story</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setShowHTML(prev => ({ ...prev, contentEn: !prev.contentEn }))}
                    title="Edit HTML source"
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Box>
                {showHTML.contentEn ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    variant="outlined"
                    value={form.content.en || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, content: { ...prev.content, en: e.target.value } }))}
                    className={classes.field}
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={form.content.en || ''}
                    onChange={(val) => setForm(prev => ({ ...prev, content: { ...prev.content, en: val } }))}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write chapter story in English..."
                  />
                )}
              </Box>

              <Box className={classes.editorContainer} style={{ marginTop: 24 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography className={classes.editorLabel}>Bangla Chapter Story</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setShowHTML(prev => ({ ...prev, contentBn: !prev.contentBn }))}
                    title="Edit HTML source"
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Box>
                {showHTML.contentBn ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    variant="outlined"
                    value={form.content.bn || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, content: { ...prev.content, bn: e.target.value } }))}
                    className={classes.field}
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={form.content.bn || ''}
                    onChange={(val) => setForm(prev => ({ ...prev, content: { ...prev.content, bn: val } }))}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="এই অধ্যায়ের কাহিনী বাংলায় লিখুন..."
                  />
                )}
              </Box>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} disabled={saving} style={{ borderRadius: 10, textTransform: 'none', marginRight: 8 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
          style={{
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 24px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#ffffff',
          }}
        >
          {saving ? 'Saving...' : (isEdit ? 'Update Chapter' : 'Save Chapter')}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
}
